// Canonical platform handling shared across the app so the SAME game shows the
// SAME platforms everywhere (card, detail, browse). RAWG/IGDB return many raw
// platform entries (e.g. "PC", "PlayStation 5", "PlayStation 4", ... 40+), which
// is what produced confusing labels like "PC +48". We collapse those raw entries
// into a small, stable set of platform *families* and keep the full list around
// for tooltips.

const FAMILY_RULES = [
  { family: "PC", icon: "🖥️", match: ["pc", "windows", "mac", "macos", "os x", "linux", "steam", "gog", "epic"] },
  { family: "PlayStation", icon: "🎮", match: ["playstation", "ps5", "ps4", "ps3", "ps2", "ps1", "psp", "ps vita", "vita", "psvr"] },
  { family: "Xbox", icon: "🎮", match: ["xbox"] },
  { family: "Nintendo", icon: "🎮", match: ["nintendo", "switch", "wii", "3ds", " ds", "gamecube", "n64", "snes", "nes", "game boy", "gameboy", "gba"] },
  { family: "Mobile", icon: "📱", match: ["ios", "android", "iphone", "ipad", "mobile"] },
  { family: "Web", icon: "🌐", match: ["web", "browser"] },
  { family: "VR", icon: "🕶️", match: ["oculus", "quest", "vr", "valve index", "steamvr"] },
];

// Pull a readable name out of the many possible RAWG/IGDB shapes.
export const platformName = (p) =>
  p?.platform?.name || p?.name || (typeof p === "string" ? p : "");

const familyFor = (name) => {
  const n = (name || "").toLowerCase();
  for (const rule of FAMILY_RULES) {
    if (rule.match.some((m) => n.includes(m))) return rule.family;
  }
  return null;
};

/**
 * Normalize a raw platforms value (array or JSON string) into:
 *   { families: [{ family, icon, members: [names] }], names: [all names], count }
 * `families` is deduped and ordered by FAMILY_RULES. Anything unrecognized is
 * grouped under an "Other" family so nothing is silently dropped.
 */
export const getPlatformFamilies = (rawPlatforms) => {
  let list = rawPlatforms;
  if (typeof list === "string") {
    try {
      list = JSON.parse(list);
    } catch {
      list = [];
    }
  }
  if (!Array.isArray(list)) list = [];

  const names = list.map(platformName).filter(Boolean);

  const order = [...FAMILY_RULES.map((r) => r.family), "Other"];
  const map = new Map();
  for (const name of names) {
    const fam = familyFor(name) || "Other";
    if (!map.has(fam)) map.set(fam, new Set());
    map.get(fam).add(name);
  }

  const families = order
    .filter((fam) => map.has(fam))
    .map((fam) => ({
      family: fam,
      icon: FAMILY_RULES.find((r) => r.family === fam)?.icon || "🎮",
      members: Array.from(map.get(fam)),
    }));

  return { families, names, count: names.length };
};
